import * as THREE from 'three';

export const INSERT_PREVIEW_COLOR = 0xff3b30;
export const SNAP_PREVIEW_COLOR = 0xa9cf21;

const PREVIEW_EDGE_MARGIN = 3;
const PREVIEW_HEIGHT_MARGIN = 2;
const PREVIEW_MIN_HEIGHT = 16;
const PREVIEW_THICKNESS = 0.6;
const BACK_WALL_PREVIEW_EDGE_MARGIN = 8;
const BACK_WALL_PREVIEW_THICKNESS = 1.1;
const PREVIEW_HIGHLIGHT_THICKNESS = 0.12;
const PREVIEW_HIGHLIGHT_WIDTH_RATIO = 0.2;
const PREVIEW_OPACITY = 0.60;
const PREVIEW_RENDER_ORDER = 999;

function disposeObjectGeometry(object) {
  if (!object) {
    return;
  }

  object.traverse((child) => {
    if (child.geometry) {
      child.geometry.dispose();
    }
  });
}

function createPlateMaterial() {
  // Materials are shared across preview rebuilds. Geometry can change with the
  // target edge, but allocating GPU materials during drag is avoidable churn.
  return new THREE.MeshPhysicalMaterial({
    color: SNAP_PREVIEW_COLOR,
    transparent: true,
    opacity: PREVIEW_OPACITY,
    roughness: 0.16,
    metalness: 0.05,
    clearcoat: 0.9,
    clearcoatRoughness: 0.12,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false,
  });
}

function createBackWallPlateMaterial() {
  return new THREE.MeshPhysicalMaterial({
    color: SNAP_PREVIEW_COLOR,
    transparent: true,
    opacity: PREVIEW_OPACITY,
    roughness: 0.16,
    metalness: 0.05,
    clearcoat: 0.9,
    clearcoatRoughness: 0.12,
    side: THREE.DoubleSide,
    depthTest: true,
    depthWrite: false,
  });
}

function createHighlightMaterial() {
  return new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.28,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false,
  });
}

function createBackWallHighlightMaterial() {
  return new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.28,
    side: THREE.DoubleSide,
    depthTest: true,
    depthWrite: false,
  });
}

function getObjectBounds(object, fallbackBounds) {
  if (!object) {
    return fallbackBounds;
  }

  const box = new THREE.Box3().setFromObject(object);
  if (
    !Number.isFinite(box.min.y)
    || !Number.isFinite(box.max.y)
    || box.max.y <= box.min.y
  ) {
    return fallbackBounds;
  }

  return {
    height: box.max.y - box.min.y,
    centerY: (box.min.y + box.max.y) / 2,
  };
}

function getCombinedObjectBounds(objects, fallbackBounds) {
  const validObjects = objects.filter(Boolean);
  if (!validObjects.length) {
    return fallbackBounds;
  }

  const box = new THREE.Box3();
  let hasBounds = false;

  validObjects.forEach((object) => {
    const objectBox = new THREE.Box3().setFromObject(object);
    if (
      !Number.isFinite(objectBox.min.y)
      || !Number.isFinite(objectBox.max.y)
      || objectBox.max.y <= objectBox.min.y
    ) {
      return;
    }

    if (!hasBounds) {
      box.copy(objectBox);
      hasBounds = true;
      return;
    }

    box.union(objectBox);
  });

  if (!hasBounds) {
    return fallbackBounds;
  }

  return {
    height: box.max.y - box.min.y,
    centerY: (box.min.y + box.max.y) / 2,
  };
}

function getFallbackBounds(modelScale, fallbackBounds) {
  const fallbackHeight = Math.max(
    fallbackBounds && fallbackBounds.height ? fallbackBounds.height : 0,
    PREVIEW_MIN_HEIGHT * modelScale
  );
  const fallbackCenterY = fallbackBounds && Number.isFinite(fallbackBounds.centerY)
    ? fallbackBounds.centerY
    : fallbackHeight / 2;

  return {
    height: fallbackHeight,
    centerY: fallbackCenterY,
  };
}

function getRoundedValue(value) {
  return Number.isFinite(value) ? Math.round(value * 1000) / 1000 : '';
}

function getPlacementPreviewKey({
  placement,
  modelScale,
  targetObject,
  fallbackObject,
  fallbackBounds,
}) {
  // If the user is still hovering the same target edge, the existing plate is
  // already correct and should not be rebuilt.
  if (!placement || !placement.hoveredLine) {
    return null;
  }

  const from = placement.hoveredLine.from.getPointPosOnWorkspace();
  const to = placement.hoveredLine.to.getPointPosOnWorkspace();
  const edge = placement.edge || {};
  const secondaryFrom = edge.secondaryLine ? edge.secondaryLine.from.getPointPosOnWorkspace() : null;
  const secondaryTo = edge.secondaryLine ? edge.secondaryLine.to.getPointPosOnWorkspace() : null;

  return [
    placement.type,
    placement.targetShape && placement.targetShape.id,
    edge.orientation,
    edge.role,
    getRoundedValue(edge.x),
    getRoundedValue(edge.y),
    getRoundedValue(from.x),
    getRoundedValue(from.y),
    getRoundedValue(to.x),
    getRoundedValue(to.y),
    edge.secondaryOrientation,
    secondaryFrom && getRoundedValue(secondaryFrom.x),
    secondaryFrom && getRoundedValue(secondaryFrom.y),
    secondaryTo && getRoundedValue(secondaryTo.x),
    secondaryTo && getRoundedValue(secondaryTo.y),
    getRoundedValue(modelScale || 1),
    targetObject && targetObject.uuid,
    fallbackObject && fallbackObject.uuid,
    fallbackBounds && getRoundedValue(fallbackBounds.height),
    fallbackBounds && getRoundedValue(fallbackBounds.centerY),
  ].join('|');
}

function getEdgeOffset(placement, plateThickness) {
  const targetBounds = placement.targetBounds || {};
  const edge = placement.edge || {};
  const threshold = 0.5;

  if (edge.role === 'back-wall' && edge.previewOffsetDirection) {
    return {
      x: edge.previewOffsetDirection.x * plateThickness * 1.1,
      z: edge.previewOffsetDirection.y * plateThickness * 1.1,
    };
  }

  if (edge.orientation === 'vertical') {
    const direction = Math.abs(edge.x - targetBounds.left) <= threshold ? -1 : 1;
    return {
      x: direction * plateThickness * 0.75,
      z: 0,
    };
  }

  const direction = Math.abs(edge.y - targetBounds.top) <= threshold ? -1 : 1;
  return {
    x: 0,
    z: direction * plateThickness * 0.75,
  };
}

function getOffsetFromDirection(previewOffsetDirection, plateThickness) {
  if (!previewOffsetDirection) {
    return { x: 0, z: 0 };
  }

  return {
    x: previewOffsetDirection.x * plateThickness * 1.1,
    z: previewOffsetDirection.y * plateThickness * 1.1,
  };
}

function createPlacementPreviewPlate({
  placement,
  modelScale,
  targetObject,
  fallbackObject,
  fallbackBounds,
  plateMaterial,
  backWallPlateMaterial,
  highlightMaterial,
  backWallHighlightMaterial,
}) {
  if (!placement || !placement.hoveredLine) {
    return null;
  }

  const resolvedModelScale = modelScale || 1;
  const from = placement.hoveredLine.from.getPointPosOnWorkspace();
  const to = placement.hoveredLine.to.getPointPosOnWorkspace();
  const isBackWallPreview = placement.edge && placement.edge.role === 'back-wall';
  const fallbackPreviewBounds = getFallbackBounds(resolvedModelScale, fallbackBounds);
  const previewBounds = isBackWallPreview
    ? getCombinedObjectBounds([targetObject, fallbackObject], fallbackPreviewBounds)
    : getObjectBounds(targetObject || fallbackObject, fallbackPreviewBounds);
  const plateColor = placement.type === 'insert' ? INSERT_PREVIEW_COLOR : SNAP_PREVIEW_COLOR;
  const edgeMargin = isBackWallPreview ? BACK_WALL_PREVIEW_EDGE_MARGIN : PREVIEW_EDGE_MARGIN;
  const plateLength = Math.hypot(to.x - from.x, to.y - from.y) * resolvedModelScale
    + (edgeMargin * 2 * resolvedModelScale);
  const plateHeight = previewBounds.height + (PREVIEW_HEIGHT_MARGIN * 2 * resolvedModelScale);
  const plateThickness = (isBackWallPreview ? BACK_WALL_PREVIEW_THICKNESS : PREVIEW_THICKNESS) * resolvedModelScale;
  const resolvedPlateMaterial = isBackWallPreview ? backWallPlateMaterial : plateMaterial;
  const resolvedHighlightMaterial = isBackWallPreview ? backWallHighlightMaterial : highlightMaterial;
  const plateCenterX = ((from.x + to.x) / 2) * resolvedModelScale;
  const plateCenterZ = ((from.y + to.y) / 2) * resolvedModelScale;
  const isVerticalEdge = placement.edge && placement.edge.orientation === 'vertical';
  const edgeOffset = getEdgeOffset(placement, plateThickness);
  const group = new THREE.Group();
  const plateGeometry = isVerticalEdge
    ? new THREE.BoxGeometry(plateThickness, plateHeight, plateLength)
    : new THREE.BoxGeometry(plateLength, plateHeight, plateThickness);
  resolvedPlateMaterial.color.setHex(plateColor);
  const plate = new THREE.Mesh(plateGeometry, resolvedPlateMaterial);
  const highlightLength = plateLength * PREVIEW_HIGHLIGHT_WIDTH_RATIO;
  const highlightThickness = PREVIEW_HIGHLIGHT_THICKNESS * resolvedModelScale;
  const highlightGeometry = isVerticalEdge
    ? new THREE.BoxGeometry(highlightThickness, plateHeight * 0.92, highlightLength)
    : new THREE.BoxGeometry(highlightLength, plateHeight * 0.92, highlightThickness);
  const highlight = new THREE.Mesh(highlightGeometry, resolvedHighlightMaterial);

  plate.position.set(
    plateCenterX + edgeOffset.x,
    previewBounds.centerY,
    plateCenterZ + edgeOffset.z
  );
  highlight.position.copy(plate.position);

  if (isVerticalEdge) {
    highlight.position.x += Math.sign(edgeOffset.x || 1) * plateThickness * 0.45;
    highlight.position.z -= plateLength * 0.18;
  } else {
    highlight.position.z += Math.sign(edgeOffset.z || 1) * plateThickness * 0.45;
    highlight.position.x -= plateLength * 0.18;
  }

  group.add(plate);
  group.add(highlight);

  if (isBackWallPreview && placement.edge.secondaryLine) {
    const secondaryFrom = placement.edge.secondaryLine.from.getPointPosOnWorkspace();
    const secondaryTo = placement.edge.secondaryLine.to.getPointPosOnWorkspace();
    const isSecondaryVerticalEdge = placement.edge.secondaryOrientation === 'vertical';
    const secondaryPlateLength = Math.hypot(secondaryTo.x - secondaryFrom.x, secondaryTo.y - secondaryFrom.y) * resolvedModelScale
      + (edgeMargin * 2 * resolvedModelScale);
    const secondaryPlateCenterX = ((secondaryFrom.x + secondaryTo.x) / 2) * resolvedModelScale;
    const secondaryPlateCenterZ = ((secondaryFrom.y + secondaryTo.y) / 2) * resolvedModelScale;
    const secondaryEdgeOffset = getOffsetFromDirection(placement.edge.secondaryPreviewOffsetDirection, plateThickness);
    const secondaryPlateGeometry = isSecondaryVerticalEdge
      ? new THREE.BoxGeometry(plateThickness, plateHeight, secondaryPlateLength)
      : new THREE.BoxGeometry(secondaryPlateLength, plateHeight, plateThickness);
    const secondaryPlate = new THREE.Mesh(secondaryPlateGeometry, resolvedPlateMaterial);
    const secondaryHighlightLength = secondaryPlateLength * PREVIEW_HIGHLIGHT_WIDTH_RATIO;
    const secondaryHighlightGeometry = isSecondaryVerticalEdge
      ? new THREE.BoxGeometry(highlightThickness, plateHeight * 0.92, secondaryHighlightLength)
      : new THREE.BoxGeometry(secondaryHighlightLength, plateHeight * 0.92, highlightThickness);
    const secondaryHighlight = new THREE.Mesh(secondaryHighlightGeometry, resolvedHighlightMaterial);

    secondaryPlate.position.set(
      secondaryPlateCenterX + secondaryEdgeOffset.x,
      previewBounds.centerY,
      secondaryPlateCenterZ + secondaryEdgeOffset.z
    );
    secondaryHighlight.position.copy(secondaryPlate.position);

    if (isSecondaryVerticalEdge) {
      secondaryHighlight.position.x += Math.sign(secondaryEdgeOffset.x || 1) * plateThickness * 0.45;
      secondaryHighlight.position.z -= secondaryPlateLength * 0.18;
    } else {
      secondaryHighlight.position.z += Math.sign(secondaryEdgeOffset.z || 1) * plateThickness * 0.45;
      secondaryHighlight.position.x -= secondaryPlateLength * 0.18;
    }

    group.add(secondaryPlate);
    group.add(secondaryHighlight);
  }

  group.renderOrder = PREVIEW_RENDER_ORDER;
  group.traverse((object) => {
    object.renderOrder = isBackWallPreview ? 0 : PREVIEW_RENDER_ORDER;
  });

  return group;
}

export default class PlacementPreviewPlate {
  constructor(scene) {
    this.scene = scene;
    this.group = null;
    this.updateKey = null;
    this.plateMaterial = createPlateMaterial();
    this.backWallPlateMaterial = createBackWallPlateMaterial();
    this.highlightMaterial = createHighlightMaterial();
    this.backWallHighlightMaterial = createBackWallHighlightMaterial();
  }

  update(options) {
    const updateKey = getPlacementPreviewKey(options);

    if (this.group && this.updateKey === updateKey) {
      return;
    }

    this.clear();

    if (!this.scene || !updateKey) {
      return;
    }

    this.group = createPlacementPreviewPlate({
      ...options,
      plateMaterial: this.plateMaterial,
      backWallPlateMaterial: this.backWallPlateMaterial,
      highlightMaterial: this.highlightMaterial,
      backWallHighlightMaterial: this.backWallHighlightMaterial,
    });
    this.updateKey = updateKey;
    if (this.group) {
      this.scene.add(this.group);
    }
  }

  clear() {
    if (!this.group) {
      return;
    }

    if (this.scene) {
      this.scene.remove(this.group);
    }

    disposeObjectGeometry(this.group);
    this.group = null;
    this.updateKey = null;
  }

  dispose() {
    this.clear();
    this.plateMaterial.dispose();
    this.backWallPlateMaterial.dispose();
    this.highlightMaterial.dispose();
    this.backWallHighlightMaterial.dispose();
    this.scene = null;
  }
}
